/**
 * Wire types — keep in sync with the Worker's response shapes.
 */

export type ConversationStatus =
  | "inquired"
  | "negotiating"
  | "committed"
  | "met"
  | "paid"
  | "ghosted"
  | "declined";

export const ALL_STATUSES: ConversationStatus[] = [
  "inquired", "negotiating", "committed", "met", "paid", "ghosted", "declined",
];

export interface Account {
  id: string;
  username: string;
  display_name: string | null;
  notes: string | null;
  created_at: number;
}

export interface BuyerRow {
  id: string;
  display_name: string;
  notes: string | null;
  phone: string | null;
  first_seen_at: number;
  conv_id: string | null;
  conv_status: ConversationStatus | null;
  conv_last_activity_at: number | null;
  conv_channel: string | null;
  conv_agreed_price_cents: number | null;
  conv_tickets_count: number | null;
  conv_agreed_meet_spot: string | null;
  conv_agreed_meet_at: number | null;
  conv_archived_at: number | null;
  conv_unread_count: number;
  last_message_body: string | null;
  last_message_direction: "inbound" | "outbound" | null;
  last_message_sent_at: number | null;
  account_id: string | null;
  account_username: string | null;
  listing_id: string | null;
  listing_title: string | null;
  listing_price_cents: number | null;
  /** FEST-PHASE-2.AB — cross-account dedupe flag, set by the worker
   *  in /api/buyers when a row matches another buyer by phone or
   *  normalized display_name. Points at the canonical primary's id.
   *  null/undefined means "canonical row" or "no duplicates."
   *  duplicate_match tells the UI WHY they were grouped — "phone" is
   *  high-confidence, "name" is best-effort.
   *  duplicate_primary_name is the primary's display_name so the chip
   *  can show "DUPE of <name>" without a second lookup. */
  duplicate_of?: string | null;
  duplicate_match?: "phone" | "name" | null;
  duplicate_primary_name?: string | null;
}

export interface PatchConversationBody {
  tickets_count?: number | null;
  agreed_price_cents?: number | null;
  status?: ConversationStatus;
  agreed_meet_spot?: string | null;
  agreed_meet_at?: number | null;
  account_id?: string;
  archived?: boolean;
}

export interface PatchBuyerBody {
  phone?: string | null;
  notes?: string | null;
}

export interface Message {
  id:              string;
  conversation_id: string;
  direction:       "inbound" | "outbound";
  body:            string;
  raw_email_id:    string | null;
  sent_at:         number;

  /** Client-only state for optimistic outbound bubbles (FEST-PHASE-2.L).
   *  Server payloads never carry these fields. */
  local_status?:   "sending" | "failed";
  local_temp_id?:  string;
}

export interface SendReplyResponse {
  ok:                boolean;
  message_id:        string;
  resend_message_id: string;
  sent_by:           string;
  sent_at:           number;
}

export interface Template {
  id:          string;
  label:       string;
  body:        string;
  intent_tags: string | null;   // JSON-encoded string array
  use_count:   number;
  updated_at:  number;
}
